import { Injectable } from '@angular/core';
import { LogError, LogStatus, Metadata, UserInfo } from '@memberjunction/core';
import { PortalAccessLogEntity } from 'mj_generatedentities';
import { DataFetchService } from './data-fetch.service';
import { UserService } from './user.service';
import { Utils } from '../utils/utils';
import { EntityNames } from '../utils/constants';

/**
 * Portal Access Log Service
 *
 * Logs user access events (login, logout, session expiry) to the Chapter Portal
 * with comprehensive Okta authentication data and browser/network information.
 *
 * Features:
 * - Login event logging with Okta claims
 * - Logout event logging with session duration
 * - Session expiry logging
 * - IP address detection via Utils service
 * - Browser user agent capture
 * - Impersonation session tracking
 *
 * @example
 * // Log user login
 * await portalAccessLogService.logLogin(userInfo, claims, impersonationSessionId);
 *
 * // Log user logout
 * const duration = portalAccessLogService.getSessionDuration();
 * await portalAccessLogService.logLogout(userInfo, duration);
 *
 * // Log session expiry
 * await portalAccessLogService.logSessionExpired(userInfo, duration);
 */
@Injectable({
  providedIn: 'root'
})
export class PortalAccessLogService {
  private readonly SESSION_LOGIN_TIMESTAMP_KEY = 'portal_login_timestamp';

  constructor(
    private dataFetchService: DataFetchService,
    private userService: UserService,
    private utils: Utils
  ) {}

  /**
   * Log user login event
   *
   * Captures comprehensive authentication data including Okta claims,
   * IP address, user agent, and session start time.
   *
   * @param userInfo - MemberJunction UserInfo object
   * @param claims - Okta authentication claims
   * @param impersonationSessionId - Optional impersonation session ID
   */
  async logLogin(
    userInfo: UserInfo,
    claims: any,
    impersonationSessionId?: string
  ): Promise<void> {
    try {
      // Guard: Skip if we've already logged this session's login
      const existingTimestamp = sessionStorage.getItem(this.SESSION_LOGIN_TIMESTAMP_KEY);
      if (existingTimestamp) {
        LogStatus(`Login already logged for this session (timestamp: ${existingTimestamp}) - skipping duplicate log`);
        return;
      }

      LogStatus(`Logging login event for user: ${userInfo.Email}`);

      // Create entity instance
      const md = new Metadata();
      const logEntity = await md.GetEntityObject<PortalAccessLogEntity>(
        EntityNames.PortalAccessLogs,
        userInfo
      );

      if (!logEntity) {
        throw new Error('Failed to create PortalAccessLog entity');
      }

      // Extract Okta data from claims
      const personId = claims?.personId ? String(claims.personId) : null;
      const auraUniqueId = claims?.auraUniqueId;
      const email = claims?.email || claims?.preferred_username;
      const fullName = claims?.name ||
        `${claims?.given_name || ''} ${claims?.family_name || ''}`.trim();
      const userRole = claims?.userRole;

      // Populate entity fields
      logEntity.User_ID = userInfo.ID;
      logEntity.PersonID = personId;
      logEntity.AuraUniqueID = auraUniqueId;
      logEntity.Email = email;
      logEntity.FullName = fullName;
      logEntity.UserRole = userRole;
      logEntity.EventType = 'Login';
      logEntity.ClientIP = await this.utils.getClientIP();
      logEntity.UserAgent = this.getUserAgent();
      logEntity.OktaTokenClaims = this.extractOktaClaims(claims);
      logEntity.ImpersonationSessionID = impersonationSessionId || null;
      logEntity.SessionDuration = null; // No duration for login events

      // Save entity
      const success = await logEntity.Save();

      if (!success) {
        throw new Error(`Failed to save login event: ${logEntity.LatestResult?.Message || 'Unknown error'}`);
      }

      // Store login timestamp for session duration tracking
      this.storeLoginTimestamp();

      LogStatus(`Login event logged successfully for ${email}, ID: ${logEntity.ID}`);
    } catch (error) {
      LogError('Failed to log login event:', undefined, error);
      // Don't throw - logging should never block user actions
    }
  }

  /**
   * Log user logout event
   *
   * Captures logout event with calculated session duration.
   *
   * @param userInfo - MemberJunction UserInfo object
   * @param sessionDuration - Session duration in seconds
   * @param impersonationSessionId - Optional impersonation session ID
   */
  async logLogout(
    userInfo: UserInfo,
    sessionDuration: number,
    impersonationSessionId?: string
  ): Promise<void> {
    try {
      LogStatus(`Logging logout event for user: ${userInfo.Email}`);

      // Create entity instance
      const md = new Metadata();
      const logEntity = await md.GetEntityObject<PortalAccessLogEntity>(
        EntityNames.PortalAccessLogs,
        userInfo
      );

      if (!logEntity) {
        throw new Error('Failed to create PortalAccessLog entity');
      }

      // Populate entity fields
      logEntity.User_ID = userInfo.ID;
      logEntity.PersonID = null; // No Okta claims available on logout
      logEntity.AuraUniqueID = null;
      logEntity.Email = userInfo.Email;
      logEntity.FullName = userInfo.Name;
      logEntity.UserRole = null; // Could be retrieved from UserService if needed
      logEntity.EventType = 'Logout';
      logEntity.ClientIP = await this.utils.getClientIP();
      logEntity.UserAgent = this.getUserAgent();
      logEntity.OktaTokenClaims = null; // No claims on logout
      logEntity.ImpersonationSessionID = impersonationSessionId || null;
      logEntity.SessionDuration = sessionDuration;

      // Save entity
      const success = await logEntity.Save();

      if (!success) {
        throw new Error(`Failed to save logout event: ${logEntity.LatestResult?.Message || 'Unknown error'}`);
      }

      // Clear login timestamp
      this.clearLoginTimestamp();

      LogStatus(`Logout event logged successfully for ${userInfo.Email}, session duration: ${sessionDuration}s, ID: ${logEntity.ID}`);
    } catch (error) {
      LogError('Failed to log logout event:', undefined, error);
      // Don't throw - logging should never block user actions
    }
  }

  /**
   * Log session expiry event
   *
   * Captures automatic session expiry with calculated session duration.
   * Similar to logout but with different event type.
   *
   * @param userInfo - MemberJunction UserInfo object
   * @param sessionDuration - Session duration in seconds
   * @param impersonationSessionId - Optional impersonation session ID
   */
  async logSessionExpired(
    userInfo: UserInfo,
    sessionDuration: number,
    impersonationSessionId?: string
  ): Promise<void> {
    try {
      LogStatus(`Logging session expiry event for user: ${userInfo.Email}`);

      // Create entity instance
      const md = new Metadata();
      const logEntity = await md.GetEntityObject<PortalAccessLogEntity>(
        EntityNames.PortalAccessLogs,
        userInfo
      );

      if (!logEntity) {
        throw new Error('Failed to create PortalAccessLog entity');
      }

      // Populate entity fields
      logEntity.User_ID = userInfo.ID;
      logEntity.PersonID = null; // No Okta claims available on expiry
      logEntity.AuraUniqueID = null;
      logEntity.Email = userInfo.Email;
      logEntity.FullName = userInfo.Name;
      logEntity.UserRole = null;
      logEntity.EventType = 'SessionExpired';
      logEntity.ClientIP = await this.utils.getClientIP();
      logEntity.UserAgent = this.getUserAgent();
      logEntity.OktaTokenClaims = null; // No claims on expiry
      logEntity.ImpersonationSessionID = impersonationSessionId || null;
      logEntity.SessionDuration = sessionDuration;

      // Save entity
      const success = await logEntity.Save();

      if (!success) {
        throw new Error(`Failed to save session expiry event: ${logEntity.LatestResult?.Message || 'Unknown error'}`);
      }

      // Clear login timestamp
      this.clearLoginTimestamp();

      LogStatus(`Session expiry event logged successfully for ${userInfo.Email}, session duration: ${sessionDuration}s, ID: ${logEntity.ID}`);
    } catch (error) {
      LogError('Failed to log session expiry event:', undefined, error);
      // Don't throw - logging should never block user actions
    }
  }

  /**
   * Get browser user agent string
   *
   * @returns User agent string or null if not available
   */
  private getUserAgent(): string | null {
    return typeof navigator !== 'undefined' ? navigator.userAgent : null;
  }

  /**
   * Extract relevant Okta claims for logging
   *
   * Extracts only necessary claims from the Okta token, excluding
   * sensitive data like raw tokens. Returns formatted JSON string.
   *
   * @param claims - Okta authentication claims object
   * @returns JSON string of relevant claims
   */
  private extractOktaClaims(claims: any): string {
    try {
      const relevantClaims = {
        personId: claims?.personId,
        auraUniqueId: claims?.auraUniqueId,
        email: claims?.email || claims?.preferred_username,
        name: claims?.name || `${claims?.given_name || ''} ${claims?.family_name || ''}`.trim(),
        userRole: claims?.userRole,
        iss: claims?.iss,  // Okta issuer
        aud: claims?.aud,  // Audience
        auth_time: claims?.auth_time,  // Authentication time
        exp: claims?.exp,  // Token expiry
      };

      return JSON.stringify(relevantClaims, null, 2);
    } catch (error) {
      LogError('Failed to extract Okta claims:', undefined, error);
      return JSON.stringify({ error: 'Failed to extract claims' });
    }
  }

  /**
   * Calculate session duration from stored login timestamp
   *
   * Retrieves the login timestamp from sessionStorage and calculates
   * the duration in seconds. Returns 0 if timestamp not found.
   *
   * @returns Session duration in seconds
   */
  getSessionDuration(): number {
    try {
      const loginTimestamp = sessionStorage.getItem(
        this.SESSION_LOGIN_TIMESTAMP_KEY
      );

      if (!loginTimestamp) {
        LogStatus('No login timestamp found in sessionStorage');
        return 0;
      }

      const loginTime = parseInt(loginTimestamp, 10);
      if (isNaN(loginTime)) {
        LogError('Invalid login timestamp format:', undefined, loginTimestamp);
        return 0;
      }

      const durationMs = Date.now() - loginTime;
      const durationSeconds = Math.floor(durationMs / 1000);

      LogStatus(`Session duration calculated: ${durationSeconds} seconds (${Math.floor(durationSeconds / 60)} minutes)`);

      return durationSeconds;
    } catch (error) {
      LogError('Failed to calculate session duration:', undefined, error);
      return 0;
    }
  }

  /**
   * Store login timestamp in sessionStorage
   *
   * Stores the current timestamp when user logs in, used later
   * to calculate session duration on logout or expiry.
   */
  private storeLoginTimestamp(): void {
    try {
      const timestamp = Date.now().toString();
      sessionStorage.setItem(this.SESSION_LOGIN_TIMESTAMP_KEY, timestamp);
      LogStatus(`Login timestamp stored: ${timestamp} (${new Date(Date.now()).toISOString()})`);
    } catch (error) {
      LogError('Failed to store login timestamp:', undefined, error);
    }
  }

  /**
   * Clear login timestamp from sessionStorage
   *
   * Removes the login timestamp when user logs out or session expires.
   */
  private clearLoginTimestamp(): void {
    try {
      sessionStorage.removeItem(this.SESSION_LOGIN_TIMESTAMP_KEY);
      LogStatus('Login timestamp cleared from sessionStorage');
    } catch (error) {
      LogError('Failed to clear login timestamp:', undefined, error);
    }
  }
}
